import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Alert from '../components/common/Alert';
import './MonthAnalytics.css';

// Paths relative to axios.defaults.baseURL (/api) — do NOT prefix REACT_APP_API_URL
// or requests become /api/api/... and 404 with "Route not found".

const MonthAnalytics = () => {
  // Set default dates to current year
  const currentYear = new Date().getFullYear();
  const defaultStartDate = `${currentYear}-01-01`;
  const defaultEndDate = `${currentYear}-12-31`;
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [fieldName, setFieldName] = useState('lastUpdatedAt');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fieldOptions = [
    { value: 'lastUpdatedAt', label: 'Last Updated At' },
    { value: 'firstOrderDate', label: 'First Order Date' },
    { value: 'lastOrderDate', label: 'Last Order Date' },
    { value: 'createdOn', label: 'Created On' },
    { value: 'abandonedCartLastUpdated', label: 'Abandoned Cart Last Updated' }
  ];

  const fetchAnalyticsData = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start >= end) {
      setError('Start date must be before end date');
      return;
    }

    if (!fieldName) {
      setError('Please select a field filter');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await axios.get('/dashboard/analytics/monthly', {
        params: {
          startDate: startDate,
          endDate: endDate,
          fieldName: fieldName
        }
      });

      if (response.data.success) {
        setAnalyticsData(response.data.data);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'An error occurred while fetching analytics data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAnalyticsData();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading && startDate && endDate && fieldName) {
      handleSearch();
    }
  };

  const formatMonth = (monthString) => {
    // monthString format: "2025-01"
    const [year, month] = monthString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const downloadCSV = () => {
    if (!analyticsData || analyticsData.length === 0) return;

    const headers = ['Month', 'Count'];
    const rows = analyticsData.map(item => [
      formatMonth(item.month),
      item.value.toString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const selectedField = fieldOptions.find(f => f.value === fieldName)?.label || fieldName;
    link.setAttribute('download', `month_analytics_${selectedField.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      className="month-analytics-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header
        title="Month Analytics"
        subtitle="Analyze monthly trends for customer fields"
        showBackButton={true}
        backPath="/menu"
      />
      <div className="month-analytics-content">
        <div className="month-analytics-controls">
          <div className="date-inputs">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onKeyPress={handleKeyPress}
              className="date-input"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onKeyPress={handleKeyPress}
              className="date-input"
              placeholder="End Date"
              min={startDate || undefined}
            />
          </div>
          <select
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            className="field-select"
            onKeyPress={handleKeyPress}
          >
            {fieldOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <button 
            onClick={handleSearch}
            className="search-button"
            disabled={loading || !startDate || !endDate || !fieldName}
          >
            {loading ? 'Loading...' : 'Show Analytics'}
          </button>
          
          <button 
            onClick={downloadCSV}
            className="download-button"
            disabled={!analyticsData || analyticsData.length === 0}
          >
            Download CSV
          </button>
        </div>

        {error && <Alert severity="error" message={error} />}

        {loading ? (
          <div className="analytics-table-container skeleton-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(12)].map((_, index) => (
                  <tr key={`skeleton-${index}`} className="skeleton-row">
                    <td>
                      <div className="skeleton-line skeleton-medium"></div>
                    </td>
                    <td>
                      <div className="skeleton-line skeleton-small"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : analyticsData && analyticsData.length > 0 ? (
          <div className="analytics-table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.map((item, index) => (
                  <tr key={index}>
                    <td>{formatMonth(item.month)}</td>
                    <td>{item.value.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : hasSearched ? (
          <div className="no-data-message">
            <div className="no-data-icon">📊</div>
            <div>No analytics data available for the selected date range and field</div>
            <div className="no-data-subtitle">Try adjusting your date range or field filter</div>
          </div>
        ) : (
          <div className="welcome-message">
            <div className="welcome-icon">📈</div>
            <div className="welcome-title">Ready to Analyze Monthly Trends</div>
            <div className="welcome-subtitle">
              Select your date range and field filter, then click "Show Analytics" to begin
            </div>
            <div className="welcome-tips">
              <div className="tip">💡 <strong>Tip:</strong> Larger date ranges may take longer to process</div>
              <div className="tip">📊 <strong>Field Options:</strong> Choose from Last Updated At, First Order Date, Last Order Date, First Login At, or Abandoned Cart Last Updated</div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MonthAnalytics;
